/**
 * Detects non-actionable errors thrown by a script the client injected as a
 * `blob:` URL. Extensions and automation harnesses that inject code via
 * `URL.createObjectURL` produce a blob on the page's own origin rather than a
 * `chrome-extension://` URL, so `isBrowserExtensionStackNoise` never sees an
 * extension scheme and Datadog RUM reports the throw as first-party.
 *
 * The frames these produce carry a real location but no attributable function:
 *
 *     SyntaxError: Unexpected token .
 *       at undefined @ blob:https://app.marblehealth.com/<uuid>:1:758
 *
 * That shape slips past the neighbouring filters. `isSourcelessScriptErrorNoise`
 * wants an `@ undefined` frame — a missing *source location* — whereas this is
 * `at undefined @ blob:…`, a missing *function name* with a real location.
 * `isCrossOriginScriptError` already reads `at undefined @ ` as
 * non-attributable, but only for the browser's sanitized "Script error."
 * message.
 *
 * `apps/web` never creates a script or Worker blob, so a stack made up entirely
 * of blob frames cannot contain our code.
 *
 * Matches when:
 * 1. The stack has at least one `at …` frame, one of them a `blob:` frame
 * 2. Every frame points at a `blob:` URL or `[native code]`
 * 3. No frame references application code — if our code is on the stack, the
 *    error still pages
 * 4. The error message is short/opaque (≤ 64 chars), the same heuristic
 *    `isSourcelessScriptErrorNoise` uses to avoid suppressing application
 *    errors that happen to have corrupted stacks
 */

const APPLICATION_FRAME_MARKERS = [
  "/src/",
  "/app/",
  "/lib/",
  "/components/",
  "_next/static/chunks/",
];

export function isBlobScriptStackNoise(
  message: string | undefined,
  stack: string | undefined,
): boolean {
  if (!message || !stack) {
    return false;
  }

  if (message.length > 64) {
    return false;
  }

  const frames = stack
    .split("\n")
    .filter((line) => line.trim().startsWith("at "));

  if (frames.length === 0) {
    return false;
  }

  if (
    frames.some((frame) =>
      APPLICATION_FRAME_MARKERS.some((marker) => frame.includes(marker)),
    )
  ) {
    return false;
  }

  if (!frames.some((frame) => _isBlobFrame(frame))) {
    return false;
  }

  return frames.every(
    (frame) => _isBlobFrame(frame) || frame.includes("[native code]"),
  );
}

/**
 * Both frame spellings this codebase sees resolve to a `blob:` location: the
 * Datadog RUM `at <fn> @ blob:…` / `at undefined @ blob:…` form, and the bare
 * `at blob:…` form some browsers emit when there is no function name at all.
 */
function _isBlobFrame(frame: string): boolean {
  return frame.includes("@ blob:") || frame.trim().startsWith("at blob:");
}
